import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioDTO,
  AnalysisFeePortfolioSetupDTO,
  FamilyMemberAccountDTO,
  FamilyMemberDTO,
  FeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import * as Yup from "yup";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";

export const feeCalculatorValidators = Yup.object().shape({});

export const calculateTotalMemberAccount = (
  accounts: FamilyMemberAccountDTO[],
) => {
  return accounts.reduce((total, account) => total + (account.balance || 0), 0);
};
export const calculateTotals = (familyMembers?: FamilyMemberDTO[]) => {
  const initialValue = {
    account: 0,
    total: 0,
    idps: 0,
    super: 0,
  };
  if (!familyMembers) return initialValue;

  familyMembers.forEach((member) => {
    initialValue.account += member.familyMemberAccounts.length;

    member.familyMemberAccounts.forEach((account) => {
      if (account.isIDPS) {
        initialValue.idps += account.balance;
      } else {
        initialValue.super += account.balance;
      }
      initialValue.total += account.balance;
    });
  });

  return initialValue;
};

export const getIndexMapping = (
  feeCalculators: FeeCalculatorDTO[],
): Map<AnalysisVariable, number> => {
  let indexMapping = new Map<AnalysisVariable, number>();
  feeCalculators.forEach((feeCalculator, index) => {
    indexMapping.set(feeCalculator.variableName, index);
  });
  return indexMapping;
};

export const getLabelMapping = (
  feeCalculators: FeeCalculatorDTO[],
): Map<AnalysisVariable, { name: string; secondaryName?: string | null }> => {
  let labelMapping = new Map<
    AnalysisVariable,
    { name: string; secondaryName?: string | null }
  >();
  feeCalculators.forEach((feeCalculator) => {
    const { name, secondaryName } = feeCalculator;
    labelMapping.set(feeCalculator.variableName, {
      name: name,
      secondaryName: secondaryName,
    });
  });
  return labelMapping;
};

export const checkHasEmptyBalance = (
  familyMembers?: FamilyMemberDTO[],
): boolean => {
  if (!familyMembers) return true;
  for (let member of familyMembers) {
    if (member.totalBalance <= 0) return true;
    for (let account of member.familyMemberAccounts) {
      if (account.balance <= 0) return true;
    }
  }
  return false;
};

export const checkValidPortfolioSetup = (
  portfolioSetups: AnalysisFeePortfolioSetupDTO[],
) => {
  return !portfolioSetups.some((portfolioSetup) => {
    const {
      excludedFromFeeAnalysis,
      selectedPortfolioId,
      isMissingInvestment,
      isMissingInvestmentOption,
      hasNoInvestmentData,
      hasAllocationError,
      hasUnavailableInvestmentOption,
      hasUnavailableInvestment,
      hasCashNotAvailableInSuperError,
      hasCashNotAvailableInIDPSError,
    } = portfolioSetup;

    return (
      !excludedFromFeeAnalysis &&
      !hasNoInvestmentData &&
      (!selectedPortfolioId ||
        isMissingInvestment ||
        isMissingInvestmentOption ||
        hasAllocationError ||
        hasUnavailableInvestmentOption ||
        hasUnavailableInvestment ||
        hasCashNotAvailableInSuperError ||
        hasCashNotAvailableInIDPSError)
    );
  });
};

export const getCFSAndNorthVariablesResetConditions = ({
  isShowCFSEdgeEligibleMFBalance,
  isShowCFSEdgeA100PortfolioBalance,
  isShowCFSEdgeA30PortfolioBalance,
  isShowNorthSelectBalance,
  isShowNorthGrowBalance,
}: {
  isShowCFSEdgeEligibleMFBalance?: boolean;
  isShowCFSEdgeA100PortfolioBalance?: boolean;
  isShowCFSEdgeA30PortfolioBalance?: boolean;
  isShowNorthSelectBalance?: boolean;
  isShowNorthGrowBalance?: boolean;
}) => {
  return [
    {
      condition: !isShowCFSEdgeEligibleMFBalance,
      variable: AnalysisVariable.Edge_eligible_MF_balance,
    },
    {
      condition: !isShowCFSEdgeA100PortfolioBalance,
      variable: AnalysisVariable.Edge_A100_Portfolio_balance,
    },
    {
      condition: !isShowCFSEdgeA30PortfolioBalance,
      variable: AnalysisVariable.Edge_A30_Portfolio_balance,
    },
    {
      condition: !isShowNorthSelectBalance,
      variable: AnalysisVariable.NorthSelect_balance,
    },
    {
      condition: !isShowNorthGrowBalance,
      variable: AnalysisVariable.North_Grow_Portfolio_balance,
    },
  ];
};

export const filterPortfoliosInUse = ({
  data,
  portfolioLevel,
}: {
  data: AnalysisFeeCalculatorDTO;
  portfolioLevel: FeeCalculatorPortfolioLevel;
}): AnalysisFeePortfolioDTO[] => {
  const portfolioIdsInUse =
    portfolioLevel === FeeCalculatorPortfolioLevel.No
      ? []
      : data.portfolioSetups
          ?.map((setup) => setup.selectedPortfolioId)
          ?.filter(Boolean) ?? [];

  return (
    data.portfolios?.filter((portfolio) =>
      portfolioIdsInUse.includes(portfolio.id),
    ) ?? []
  );
};
